import {
    autoUpdate,
    flip,
    offset as floatingOffset,
    Placement,
    useFloating,
    useHover,
    useInteractions
} from '@floating-ui/react';
import cn from 'classnames';
import { Fragment, ReactElement, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

interface AdvancedTooltipProps {
    content?: string | ReactElement;
    className?: string;
    offset?: number;
    placement?: Placement;
    width?: number | string; // optional override
    children: ReactElement;
}

const AdvancedTooltip = (props: AdvancedTooltipProps) => {
    const {
        content,
        className,
        offset = 8,
        placement = 'top-end',
        width, // no default = no forced override
        children
    } = props;

    const [isOpen, setIsOpen] = useState(false);

    const middleware = useMemo(() => {
        return [floatingOffset(offset), flip({ crossAxis: false })];
    }, [offset]);

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        strategy: 'fixed',
        placement,
        middleware,
        whileElementsMounted: autoUpdate
    });

    const hover = useHover(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

    return (
        <Fragment>
            <div ref={refs.setReference} {...getReferenceProps()}>
                {children}
            </div>

            {content &&
                isOpen &&
                createPortal(
                    <div
                        ref={refs.setFloating}
                        style={{
                            ...floatingStyles,
                            ...(width ? { width } : {})
                        }}
                        className={cn(
                            'z-[100] rounded bg-brand-background-dark p-2 text-xs text-brand-neutral-500 shadow-md',
                            className
                        )}
                        {...getFloatingProps()}>
                        {content}
                    </div>,
                    document.body
                )}
        </Fragment>
    );
};

export default AdvancedTooltip;
